import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const SearchIcon = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M19.4846 20.1542L13.223 13.8926C12.723 14.3183 12.148 14.6477 11.498 14.8811C10.848 15.1144 10.1948 15.2311 9.53839 15.2311C7.93673 15.2311 6.58118 14.6767 5.47177 13.5679C4.36233 12.459 3.80762 11.1042 3.80762 9.50343C3.80762 7.90263 4.36203 6.54678 5.47084 5.43588C6.57966 4.32498 7.93447 3.76953 9.53527 3.76953C11.1361 3.76953 12.4919 4.32424 13.6028 5.43366C14.7137 6.54307 15.2692 7.89862 15.2692 9.50031C15.2692 10.1952 15.1461 10.8676 14.8999 11.5176C14.6538 12.1676 14.3307 12.7234 13.9307 13.1849L20.1923 19.4465L19.4846 20.1542ZM9.53839 14.2311C10.8653 14.2311 11.9855 13.7743 12.899 12.8609C13.8124 11.9474 14.2692 10.8272 14.2692 9.50031C14.2692 8.17339 13.8124 7.0532 12.899 6.13973C11.9855 5.22627 10.8653 4.76953 9.53839 4.76953C8.21147 4.76953 7.09128 5.22627 6.17782 6.13973C5.26437 7.0532 4.80764 8.17339 4.80764 9.50031C4.80764 10.8272 5.26437 11.9474 6.17782 12.8609C7.09128 13.7743 8.21147 14.2311 9.53839 14.2311Z"
        fill="black"
      />
    </svg>
  )
}

const ClearIcon = () => {
  return (
    <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z"
        fill="black"
      />
    </svg>
  )
}

type SuggestionTextSearchProps = {
  textSearch: string
  onChange: (key: string, value: string, type?: 'down' | 'up' | 'option' | 'text') => void
}

const SuggestionTextSearch: React.FC<SuggestionTextSearchProps> = ({ textSearch, onChange }) => {
  const [suggestionValues, setSuggestionValues] = useState([])
  const [isShowSuggestion, setIsShowSuggestion] = useState(false)
  const inputRef = useRef(null)
  const { t } = useTranslation()

  const filterSuggestion = useMemo(() => {
    if (!textSearch) return suggestionValues

    const isAlphabet = /^[a-zA-Z\s]+$/.test(textSearch)
    const normalize = (s: string) => (isAlphabet ? s.toLowerCase() : s)
    const normalizedInput = normalize(textSearch)

    return suggestionValues?.filter((suggestion: string) => normalize(suggestion).includes(normalizedInput))
  }, [textSearch, suggestionValues])

  useEffect(() => {
    const initialSuggestion = (document.getElementById('aliases') as HTMLInputElement)?.value
    if (initialSuggestion) {
      setSuggestionValues(JSON.parse(initialSuggestion || ''))
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsShowSuggestion(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleOnChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('text_search', e.target.value, 'text')
    setIsShowSuggestion(true)
  }

  const handleChooseSuggestion = (suggestion: string) => {
    onChange('text_search', suggestion, 'option')
    setIsShowSuggestion(false)
  }

  const handleClearSuggestion = () => {
    onChange('text_search', '', 'text')
    setIsShowSuggestion(false)
  }

  return (
    <div className="text-search-container" ref={inputRef}>
      {isShowSuggestion && textSearch && filterSuggestion?.length > 0 && (
        <>
          <div ref={inputRef} className="text-search-suggestion">
            {filterSuggestion?.map((suggestion, index) => (
              <button type="button" onClick={() => handleChooseSuggestion(suggestion)} className="suggestion-item" key={index}>
                <span>{suggestion}</span>
              </button>
            ))}
          </div>
        </>
      )}
      <div className="text-search-input rounded-lg">
        <div className="search-section">
          <SearchIcon />
          <input
            type="text"
            value={textSearch}
            onFocus={() => setIsShowSuggestion(true)}
            onChange={handleOnChangeInput}
            placeholder={t('top.modal_search.search_text.placeholder')}
          />
        </div>
        {textSearch && (
          <button className="clear-suggestion" type="button" onClick={handleClearSuggestion}>
            <ClearIcon />
          </button>
        )}
      </div>
    </div>
  )
}

export default memo(SuggestionTextSearch)
